import { useEffect, useState } from "react"
import Skeleton from '@mui/material/Skeleton';
import CssBaseline from '@mui/material/CssBaseline';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { alpha, useTheme } from '@mui/material/styles';

import { SignalsList } from './SignalsList';
import { UserApi } from "../../api/UserApi";
import { SignalItem } from "../../interface/SignalItem";
import { BtcMarkets } from "../../api/BtcMarkets";
import { OcoOrder } from "../../interface/OcoOrder";
import { OCOOrders } from "../../components/OCOOrders";
import { dashboardMutedTextSx } from "../dashboardv2/dashboardStyles";


export const Signals = () =>
{
  const theme = useTheme()
  const [ loading, setLoading ] = useState(false)
  const [ signals, setSignals ] = useState<SignalItem[] | undefined>([])
  const [ ocoOrders, setOCOorders ] = useState<OcoOrder[] | undefined>([])

  const [ ocoOrderRemoveStatus, setOcoOrderRemoveStatus ] = useState("")

  const getSignals = async () =>
  {
    try
    {
      const userApi = new UserApi()
      const signals = await userApi.getSignalsList()
      setSignals(signals?.sort((a, b) => b.time_generated - a.time_generated))
    }
    catch(error)
    {

    }

  }

  const getOCOOrders = async () =>
  {
    try
    {
      const orderApi = new BtcMarkets()
      const orders = await orderApi.getOCOorders()
      setOCOorders(orders)
    }
    catch(error)
    {

    }

  }

  const handleRemoveOco = async (marketId: string, orderId: string, ocoOrderId: number) =>
  {

    try
    {
      const response = await new BtcMarkets().removeOcoOrder(marketId, orderId, ocoOrderId)
      if(response)
      {
        if(response.deletedOco && response.deletedOrder) setOcoOrderRemoveStatus("Deleted order and OCO OK")
        if(!response.deletedOco && response.deletedOrder) setOcoOrderRemoveStatus("Deleted order but we couldn't find the OCO to delete")
        getOCOOrders()
      }
    }
    catch(error)
    {
      console.log(error)
      setOcoOrderRemoveStatus("Some other error occurred")
    }


  }

  const getAllData = async () =>
  {
    setLoading(true)
    await getOCOOrders()
    await getSignals()
    setLoading(false)
  }

  useEffect(() => {

    getAllData()
    
  }, [ ])


  return(
    <Box
      sx={{
        minHeight: "91vh",
        overflow: "auto",
        m: { xs: 1.5, md: 2 },
        p: { xs: 2, md: 3 },
        borderRadius: "20px",
        position: "relative",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, #06111f 0%, #091829 58%, #050c16 100%)`
            : `linear-gradient(180deg, #f3f8ff 0%, #ebf3ff 45%, #ffffff 100%)`,
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            theme.palette.mode === "dark"
              ? `radial-gradient(circle at top left, ${alpha("#22d3ee", 0.16)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#14b8a6", 0.12)}, transparent 28%)`
              : `radial-gradient(circle at top left, ${alpha("#60a5fa", 0.18)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#22c55e", 0.1)}, transparent 30%)`,
        }}
      />

      <CssBaseline />

      { loading ? <Box sx={{ position: "relative", zIndex: 1, minHeight: "90vh" }}>

        <Skeleton height={30} width="20vw" />

        <Skeleton height={50} width="50vw" />
        <Skeleton height={50} width="100%" />

        <Skeleton height={600} width="100%" style={{ marginTop: -120 }} />
        <Skeleton height={50} width="100%" style={{ marginTop: -90 }} />

      </Box> : 
      <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>
        <Grid item xs={12}>
          <Box sx={styles.hero(theme)}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} lg={7}>
                <Typography variant="overline" sx={{ letterSpacing: 2, ...dashboardMutedTextSx(theme) }}>
                  Signals Radar
                </Typography>
                <Typography sx={{ mt: 1, fontSize: { xs: 34, md: 50 }, lineHeight: 0.98, maxWidth: 720 }}>
                  Track signal flow, source quality, and active trade ideas in one feed.
                </Typography>
                <Typography sx={{ mt: 2, maxWidth: 620, fontSize: 16, ...dashboardMutedTextSx(theme) }}>
                  Review the latest listings, inspect setup quality, and monitor signal momentum without the old card clutter.
                </Typography>
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`${signals?.length || 0} total signals`} sx={styles.heroChip(theme)} />
                  <Chip label={`${signals?.filter(signal => signal.timeout > Date.now()).length || 0} active`} sx={styles.heroChip(theme)} />
                  <Chip label={`${ocoOrders?.length || 0} OCO orders`} sx={styles.heroChip(theme)} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.metricCard(theme)}>
                      <RadarRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Live feed</Typography>
                      <Typography sx={{ fontSize: 22 }}>Signal listings</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.metricCard(theme)}>
                      <InsightsRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Review mode</Typography>
                      <Typography sx={{ fontSize: 22 }}>Source and setup quality</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.metricCard(theme)}>
                      <NotificationsActiveRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Orders</Typography>
                      <Typography sx={{ fontSize: 22 }}>{ocoOrders?.length || 0} linked OCOs</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <SignalsList signals={signals} />
        </Grid>
      </Grid>

  
      }

      <Snackbar open={!!ocoOrderRemoveStatus} message={ocoOrderRemoveStatus} autoHideDuration={6000} onClose={() => setOcoOrderRemoveStatus("")}>
          <Alert severity="info" > {ocoOrderRemoveStatus} </Alert>
      </Snackbar>
   
    </Box>
  )
}

const styles = {
  hero: (theme: any) => ({
    p: { xs: 2.25, md: 3.5 },
    borderRadius: "16px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.94)} 0%, ${alpha("#081320", 0.96)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.94)} 0%, ${alpha("#e6f0ff", 0.92)} 100%)`,
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 22px 60px ${alpha("#01050d", 0.45)}`
        : `0 22px 60px ${alpha("#9db3d1", 0.2)}`,
  }),
  metricCard: (theme: any) => ({
    p: 2,
    borderRadius: "12px",
    display: "grid",
    gap: 0.5,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.5),
  }),
  heroChip: (theme: any) => ({
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.68),
    color: theme.palette.text.primary,
  }),
}
